import { ErrorMessage, LoadingSpinner } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ChangeLogActionsGrid } from "./ChangeLogActionsGrid.js";
import { ChangeLogDetailModal } from "./ChangeLogDetailModal.js";
import { ChangeLogFilterBar } from "./ChangeLogFilterBar.js";
import {
  type ChangeLogActionGroup,
  groupEntriesByCorrelation,
} from "./changeLogGrouping.js";
import type {
  ChangeLogEntityStrategy,
  ChangeLogEntry,
  ChangeLogFacetsData,
  ChangeLogFilterState,
} from "./changeLogTypes.js";

interface Props {
  /** Accumulated, newest-first entries for the current filter slice (one page set). */
  entries: ChangeLogEntry[];
  facets: ChangeLogFacetsData | null;
  loading: boolean;
  /** Network/query error message, if any. */
  error?: string;
  /** True when the viewer lacks the gating permission for this log. */
  missingPermissions: boolean;
  hasNextPage: boolean;
  onFetchMore: () => Promise<void> | void;
  filters: ChangeLogFilterState;
  setFilters: React.Dispatch<React.SetStateAction<ChangeLogFilterState>>;
  /** Aggregate-specific entity label + deep-link mapping (design D7). */
  entityStrategy: ChangeLogEntityStrategy;
}

/**
 * The shared, aggregate-agnostic change-log view (design D7): a templates-style
 * filter bar, a `correlationId`-grouped action list, and a click-to-open detail
 * modal. It owns only presentation + group/selection state — the data (entries,
 * facets, paging) and the entity label/link strategy are injected, so the same view
 * renders the quote log and the project log identically. Server-side filtering keeps
 * pagination correct; grouping is a linear pass over the newest-first entries.
 */
export function ChangeLogView({
  entries,
  facets,
  loading,
  error,
  missingPermissions,
  hasNextPage,
  onFetchMore,
  filters,
  setFilters,
  entityStrategy,
}: Props) {
  const { t } = useTranslate(["Global"]);

  const [selectedGroup, setSelectedGroup] =
    React.useState<ChangeLogActionGroup | null>(null);

  const groups = React.useMemo(
    () => groupEntriesByCorrelation(entries),
    [entries]
  );

  return (
    <Stack direction="column" spacing={2}>
      <ChangeLogFilterBar
        facets={facets}
        filters={filters}
        setFilters={setFilters}
        entityStrategy={entityStrategy}
      />

      {missingPermissions ? (
        <ErrorMessage
          message={t("You do not have permission to view this change log", {
            ns: "Global",
          })}
        />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : loading && groups.length === 0 ? (
        <LoadingSpinner />
      ) : (
        <ChangeLogActionsGrid
          groups={groups}
          hasNextPage={hasNextPage}
          loading={loading}
          onFetchMore={onFetchMore}
          onRowClick={setSelectedGroup}
          entityStrategy={entityStrategy}
        />
      )}

      {selectedGroup && (
        <ChangeLogDetailModal
          group={selectedGroup}
          entityStrategy={entityStrategy}
          handleClose={() => setSelectedGroup(null)}
        />
      )}
    </Stack>
  );
}
